/**
 * Custom hooks for Anvil CollateralVault management
 * 
 * IMPORTANT: Anvil uses account-based collateral tracking, NOT vault IDs.
 * There is NO separate "createVault" function - the first deposit creates the account.
 */

import { useReadContract, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { getContractAddress } from '@contracts/addresses'
import { CollateralVaultABI } from '@contracts/abis'
import type { Address } from 'viem'
import { toast } from 'react-hot-toast'
import { SUPPORTED_TOKENS } from '@contracts/tokens'

/**
 * Hook to get account collateral balance for a specific token
 * Returns both available and reserved balances
 */
export function useAccountCollateralBalance(
  accountAddress?: Address,
  tokenAddress?: Address
) {
  const { data, isLoading, error, refetch } = useReadContract({
    address: getContractAddress('CollateralVault'),
    abi: CollateralVaultABI,
    functionName: 'getAccountCollateralBalance',
    args: accountAddress && tokenAddress ? [accountAddress, tokenAddress] : undefined,
    query: {
      enabled: !!accountAddress && !!tokenAddress,
      // Add retry and timeout configuration
      retry: 2, // Retry failed requests twice
      retryDelay: 1000, // 1 second between retries
      staleTime: 10000, // Consider data stale after 10 seconds
      gcTime: 30000, // Garbage collect after 30 seconds (was cacheTime)
    },
  })

  return {
    availableBalance: data?.[0] as bigint | undefined,
    reservedBalance: data?.[1] as bigint | undefined,
    isLoading,
    error,
    refetch,
  }
}

/**
 * Hook to get all collateral balances for an account (all supported tokens)
 * Returns data in format: { [tokenAddress]: { available, reserved } }
 */
export function useAllAccountCollateralBalances(accountAddress?: Address) {
  
  // Filter out native ETH since it's not stored in the vault
  const depositableTokens = Object.values(SUPPORTED_TOKENS).filter(
    (t: any) => !t.isNative
  ) as Array<{ address: Address; symbol: string; decimals: number }>

  // Create an array of balance queries
  const balanceResults = depositableTokens.map(token => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const result = useAccountCollateralBalance(accountAddress, token.address)
    return {
      tokenAddress: token.address,
      tokenSymbol: token.symbol,
      ...result
    }
  })

  // Check if any are still loading
  const isLoading = balanceResults.some(r => r.isLoading)
  
  // Log errors for debugging
  const errors = balanceResults.filter(r => r.error)
  if (errors.length > 0) {
    console.warn(`⚠️ [useAllAccountCollateralBalances] ${errors.length} balance queries failed:`, 
      errors.map(e => ({ token: e.tokenSymbol, error: e.error?.message }))
    )
  }
  
  // Build data object (include tokens with 0 balance or errors as 0)
  // IMPORTANT: Use lowercase address as key for consistent lookups
  const data = balanceResults.reduce((acc, result) => {
    acc[result.tokenAddress.toLowerCase()] = {
      available: result.availableBalance || 0n,
      reserved: result.reservedBalance || 0n,
    }
    return acc
  }, {} as Record<string, { available: bigint; reserved: bigint }>)

  // Return data even if still loading some queries (after retries complete)
  // This allows UI to show partial data instead of blocking on slow queries
  const hasAnyData = Object.keys(data).length > 0

  // Collateral balances loaded for account

  return {
    data: hasAnyData ? data : undefined,
    isLoading,
  }
}

/**
 * Hook to deposit tokens to account
 * First deposit automatically creates the collateral account
 */
export function useDepositToAccount() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const deposit = async (
    accountAddress: Address,
    tokenAddresses: Address[],
    amounts: bigint[]
  ) => {
    try {
      console.log('🟢 [useDepositToAccount] Depositing to account', {
        accountAddress,
        tokenAddresses,
        amounts: amounts.map(a => a.toString())
      })
      
      writeContract({
        address: getContractAddress('CollateralVault'),
        abi: CollateralVaultABI,
        functionName: 'depositToAccount',
        args: [accountAddress, tokenAddresses, amounts],
        gas: 250000n, // Gas limit for deposits
      })
      
      console.log('🟢 [useDepositToAccount] Transaction submitted')
    } catch (err) {
      toast.error('Failed to deposit. Ensure tokens are approved and you have sufficient balance.')
      console.error('🔴 [useDepositToAccount] Error:', err)
    }
  }

  return {
    deposit,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to deposit and approve tokens for use with a collateralizable contract (e.g., LOC)
 * This is a convenience function that deposits and sets allowance in one transaction
 */
export function useDepositAndApprove() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const depositAndApprove = async (
    tokenAddresses: Address[],
    amounts: bigint[],
    collateralizableContractAddress: Address
  ) => {
    try {
      console.log('🟢 [useDepositAndApprove] Depositing and approving', {
        tokenAddresses,
        amounts: amounts.map(a => a.toString()),
        collateralizableContractAddress
      })
      
      writeContract({
        address: getContractAddress('CollateralVault'),
        abi: CollateralVaultABI,
        functionName: 'depositAndApprove',
        args: [tokenAddresses, amounts, collateralizableContractAddress],
        gas: 300000n, // Higher gas for combined operation
      })
      
      console.log('🟢 [useDepositAndApprove] Transaction submitted')
    } catch (err) {
      toast.error('Failed to deposit and approve. Ensure tokens are approved.')
      console.error('🔴 [useDepositAndApprove] Error:', err)
    }
  }

  return {
    depositAndApprove,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to withdraw tokens from account
 */
export function useWithdrawFromAccount() {
  const { writeContract, data: hash, isPending, isError, error } = useWriteContract()

  const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({
    hash,
  })

  const withdraw = async (
    tokenAddress: Address,
    amount: bigint,
    destinationAddress: Address
  ) => {
    try {
      console.log('🟢 [useWithdrawFromAccount] Withdrawing from account', {
        tokenAddress,
        amount: amount.toString(),
        destinationAddress
      })
      
      writeContract({
        address: getContractAddress('CollateralVault'),
        abi: CollateralVaultABI,
        functionName: 'withdraw',
        args: [tokenAddress, amount, destinationAddress],
        gas: 200000n, // Gas limit for withdrawals
      })
      
      console.log('🟢 [useWithdrawFromAccount] Transaction submitted')
    } catch (err) {
      toast.error('Failed to withdraw. Ensure you have sufficient available balance.')
      console.error('🔴 [useWithdrawFromAccount] Error:', err)
    }
  }

  return {
    withdraw,
    hash,
    isPending: isPending || isConfirming,
    isSuccess,
    isError,
    error,
  }
}

/**
 * Hook to check if a token is enabled for collateral
 */
export function useCollateralToken(tokenAddress?: Address) {
  const { data, isLoading, error } = useReadContract({
    address: getContractAddress('CollateralVault'),
    abi: CollateralVaultABI,
    functionName: 'getCollateralToken',
    args: tokenAddress ? [tokenAddress] : undefined,
    query: {
      enabled: !!tokenAddress,
    },
  })

  return {
    enabled: data?.[0] as boolean | undefined,
    decimals: data?.[1] as number | undefined,
    isLoading,
    error,
  }
}

/**
 * Hook to get withdrawal fee in basis points
 */
export function useWithdrawalFee() {
  const { data: feeBasisPoints, isLoading, error } = useReadContract({
    address: getContractAddress('CollateralVault'),
    abi: CollateralVaultABI,
    functionName: 'getWithdrawalFeeBasisPoints',
  })

  return {
    feeBasisPoints: feeBasisPoints as number | undefined,
    isLoading,
    error,
  }
}

// Legacy exports for backward compatibility (deprecated)
/**
 * @deprecated Use useDepositToAccount instead. Anvil has no separate vault creation.
 */
export const useCreateVault = () => {
  console.warn('⚠️ useCreateVault is deprecated. Use useDepositToAccount instead.')
  return {
    createVault: () => {
      toast.error('Vault creation not needed. Please use the deposit function.')
    },
    hash: undefined,
    isPending: false,
    isSuccess: false,
    isError: false,
    error: null,
  }
}

/**
 * @deprecated Use useDepositToAccount instead
 */
export const useDepositToVault = useDepositToAccount

/**
 * @deprecated Use useWithdrawFromAccount instead
 */
export const useWithdrawFromVault = useWithdrawFromAccount

/**
 * @deprecated Use useAccountCollateralBalance instead
 */
export const useVaultBalance = useAccountCollateralBalance

